<template>
  <div class="stretch">
    <div>
      <h2 v-if="!id">
        Create a post
      </h2>
      <h2 v-else>
        Edit post
      </h2>
    </div>

    <div
      v-if="formErrors.non_field_errors !== undefined"
      class="alert alert-danger"
      role="alert"
    >
      {{ formErrors.non_field_errors[0] }}
    </div>

    <form @submit.prevent="submitForm">
      <div class="new-post row">
        <avatar />
        <span class="name">
          {{ userData.first_name }} {{ userData.last_name }}
        </span>
        <span class="spacer" />
        <div class="form-group">
          <select
            v-model.number="formFields.group"
            required
          >
            <option
              disabled
              value="-2"
            >
              Post To:
            </option>
            <option
              value="-1"
            >
              Public
            </option>
            <option
              v-for="group in userGroups"
              :key="group.id"
              :value="group.id"
            >
              {{ group.name }}
            </option>
          </select>
          <div
            v-if="formErrors.type !== undefined"
            class="invalid-feedback"
          >
            {{ formErrors.type[0] }}
          </div>
        </div>
      </div>
      <div class="form-group">
        <select
          v-model="formFields.type"
          required
        >
          <option
            disabled
            value=""
          >
            Please select a post type
          </option>
          <option value="post">
            Basic
          </option>
          <option value="looking">
            Looking To Buy
          </option>
          <option value="listing">
            For Sale
          </option>
        </select>
        <div
          v-if="formErrors.type !== undefined"
          class="invalid-feedback"
        >
          {{ formErrors.type[0] }}
        </div>
      </div>

      <div v-if="formFields.type !== ''">
        <div
          v-if="formFields.type === 'listing'"
          class="form-group"
        >
          <select
            v-model="formFields.category"
            :required="formFields.type ==='listing'"
          >
            <option
              disabled
              value=""
            >
              Please select a post category
            </option>
            <option
              v-for="category in categories"
              :key="category.id"
              :value="category.id"
            >
              {{ category.name }}
            </option>
          </select>
          <div
            v-if="formErrors.category !== undefined"
            class="invalid-feedback"
          >
            {{ formErrors.category[0] }}
          </div>
        </div>

        <div class="form-group">
          <input
            id="title"
            v-model="formFields.title"
            type="text"
            placeholder="Enter your title here"
            required
          >
          <div
            v-if="formErrors.title !== undefined"
            class="invalid-feedback"
          >
            {{ formErrors.title[0] }}
          </div>
        </div>

        <div class="form-group">
          <textarea
            id="description"
            v-model="formFields.description"
            rows="8"
            placeholder="Enter your description here"
            @keydown.tab.prevent="tabber($event)"
          />
          <div
            v-if="formErrors.description !== undefined"
            class="invalid-feedback"
          >
            {{ formErrors.description[0] }}
          </div>
        </div>

        <div
          v-if="formFields.type === 'listing'"
          class="form-group"
        >
          <div>Status</div>
          <select
            v-model="formFields.status"
            required
          >
            <option
              disabled
              value=""
            >
              Select a status
            </option>
            <option value="active">
              Active
            </option>
            <option value="pending">
              Pending
            </option>
            <option value="sold">
              Sold
            </option>
          </select>
          <div
            v-if="formErrors.type !== undefined"
            class="invalid-feedback"
          >
            {{ formErrors.type[0] }}
          </div>
        </div>

        <div
          v-if="formFields.type === 'listing'"
          class="form-group"
        >
          <div>Tags</div>
          <input
            v-model="current_tag"
            type="text"
            placeholder="#EnterTagsHere"
            @keydown.enter.prevent="addTag(current_tag)"
            @keydown.space.prevent="addTag(current_tag)"
          >
          <tag
            v-for="(tag, index) in formFields.tags"
            :key="`tag${index}`"
            @closeClicked="formFields.tags.splice(index, 1)"
          >
            {{ tag.name }}
          </tag>
        </div>

        <div
          v-if="formFields.type === 'listing'"
          class="form-group"
        >
          <label>Enter your price here</label><br>
          <currency-input
            v-model="formFields.price"
            currency="USD"
            type="text"
          />
          <div
            v-if="formErrors.price !== undefined"
            class="invalid-feedback"
          >
            {{ formErrors.price[0] }}
          </div>
        </div>

        <div
          v-if="formFields.type !== 'looking'"
          class="form-group"
        >
          <div
            class="images-list"
          >
            <img
              v-for="(image, imageIndex) in formFields.images"
              :key="imageIndex"
              ref="image"
              class="image-preview"
              :src="image.url"
              @click="formFields.images.splice(imageIndex, 1)"
            >
          </div>
          <label
            for="image-upload"
            class="card custom-file-upload"
          >
            <image-icon /> Add Image
          </label>
          <input
            id="image-upload"
            type="file"
            multiple
            @change="onFileChanged"
          >
          <div
            v-if="formErrors.images !== undefined"
            class="invalid-feedback"
          >
            {{ formErrors.images }}
          </div>
        </div>

        <div
          v-if="formSubmitting"
          class="progress"
        >
          <div
            class="progress-value"
            :class="length"
          />
        </div>

        <button
          type="submit"
          class="btn btn-primary"
          :disabled="formSubmitting"
        >
          <template v-if="formSubmitting">
            {{ id ? 'Editing' : 'Posting' }}
          </template>
          <template v-else>
            {{ id ? 'Edit' : 'Post' }}
          </template>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { CurrencyInput } from 'vue-currency-input';
import { mapState } from 'vuex';
import Avatar from '@/components/Avatar.vue';
import Tag from '@/components/Tag.vue';

export default {
  name: 'AddPost',
  components: {
    Avatar,
    CurrencyInput,
    Tag,
  },
  props: {
    groupId: { type: Number, default: null },
  },
  data() {
    return {
      formSubmitting: false,
      formErrors: {},
      formSuccess: false,
      formFields: {
        id: null,
        type: '',
        category: '',
        title: '',
        description: '',
        status: 'active',
        price: 0,
        group: '-1',
        images: [],
        tags: [],
      },
      current_tag: '',
    };
  },
  computed: {
    ...mapState([
      'feedItems',
    ]),
    userData() {
      return this.$store.state.userData;
    },
    categories() {
      return this.$store.state.categories.results;
    },
    id() {
      return this.$route.params.post_id;
    },
    length() {
      return {
        long: this.formFields.images.length > 1,
        medium: this.formFields.images.length === 1,
        short: this.formFields.images.length === 0,
      };
    },
  },
  async mounted() {
    if (this.id) {
      const response = await this.api({ url: `posts/${this.id}` });
      if (response.status === 200) {
        response.body.price = parseFloat(response.body.price);
        if (this.userData.id !== response.body.user.id) {
          this.formErrors = {
            non_field_errors: [
              'You do not have permissions to edit this post as you '
              + 'are not the owner.',
            ],
          };
        } else {
          this.formFields = response.body;
          if (!this.formFields.group) this.formFields.group = '-1';
        }
      } else {
        this.formErrors = {
          non_field_errors: [
            'Unable to communicate with the server. Please check your '
            + 'connection and try again.',
          ],
        };
      }
    }
    if (this.$route.name === 'group_detail') {
      this.formFields.group = this.groupId;
    }
  },
  methods: {
    getJpegOrientation(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const view = new DataView(event.target.result);
          if (view.getUint16(0, false) !== 0xFFD8) {
            return reject(-2); // eslint-disable-line prefer-promise-reject-errors
          }
          const length = view.byteLength;
          let offset = 2;
          while (offset < length) {
            const marker = view.getUint16(offset, false);
            offset += 2;
            if (marker === 0xFFE1) {
              // eslint-disable-next-line no-cond-assign
              if (view.getUint32(offset += 2, false) !== 0x45786966) {
                return resolve(-1);
              }
              const little = view.getUint16(offset += 6, false) === 0x4949;
              offset += view.getUint32(offset + 4, little);
              const tags = view.getUint16(offset, little);
              offset += 2;
              for (let i = 0; i < tags; i += 1) {
                if (view.getUint16(offset + (i * 12), little) === 0x0112) {
                  return resolve(view.getUint16(offset + (i * 12) + 8, little));
                }
              }
            } else if ((marker & 0xFF00) !== 0xFF00) { // eslint-disable-line no-bitwise
              break;
            } else {
              offset += view.getUint16(offset, false);
            }
          }
          return resolve(-1);
        };
        reader.readAsArrayBuffer(file.slice(0, 64 * 1024));
      });
    },
    blobToImage(blob) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        const image = new Image();
        image.onload = () => resolve(image);
        reader.onload = ({ target: { result: dataURL } }) => {
          image.src = dataURL;
        };
        reader.readAsDataURL(blob);
      });
    },
    async onFileChanged(event) {
      const futures = Array.prototype.map.call(event.target.files, async (file) => {
        let jpegOrientation = -1;
        let browserHonorsExifOrientation;

        if (this.iOS) {
          browserHonorsExifOrientation = true;
        } else {
          browserHonorsExifOrientation = CSS.supports('image-orientation: none');
        }

        if (!browserHonorsExifOrientation) {
          try {
            jpegOrientation = await this.getJpegOrientation(this.attachment.file);
          } catch (e) {
            jpegOrientation = e;
          }
        }

        const image = await this.blobToImage(file);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const { width, height } = image;
        if (jpegOrientation > 4 && jpegOrientation < 9) {
          canvas.width = height;
          canvas.height = width;
        } else {
          canvas.width = width;
          canvas.height = height;
        }
        switch (jpegOrientation) {
          case 2: ctx.transform(-1, 0, 0, 1, width, 0); break;
          case 3: ctx.transform(-1, 0, 0, -1, width, height); break;
          case 4: ctx.transform(1, 0, 0, -1, 0, height); break;
          case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;
          case 6: ctx.transform(0, 1, -1, 0, height, 0); break;
          case 7: ctx.transform(0, -1, -1, 0, height, width); break;
          case 8: ctx.transform(0, -1, 1, 0, 0, width); break;
          default: break;
        }
        ctx.drawImage(image, 0, 0, width, height);
        const newImageUrl = canvas.toDataURL('image/jpeg'); // This is the image with correct orientation

        this.formFields.images.push({ url: newImageUrl });
      });
      await Promise.all(futures);
    },
    async submitForm() {
      this.formErrors = {};
      this.formSubmitting = true;

      const body = {
        type: this.formFields.type,
        category: this.formFields.category,
        title: this.formFields.title,
        description: this.formFields.description,
        price: this.formFields.price,
        status: this.formFields.status,
        images: this.formFields.images,
        tags: this.formFields.tags,
        group: this.formFields.group,
      };

      if (body.group === '-1') delete body.group;

      const responseData = await this.api({
        url: this.id ? `posts/${this.id}/` : 'posts/',
        method: this.id ? 'PATCH' : 'POST',
        json: body,
      });

      this.formSubmitting = false;

      if (responseData.status === 201 || responseData.status === 200) {
        this.formSuccess = true;
        if (this.$route.name === 'home' || this.$route.name === 'group_detail') {
          const newFeedItems = this.feedItems;
          newFeedItems.unshift(responseData.body);
          this.$store.dispatch('set_feed_items', newFeedItems);

          this.$emit('close');
        } else {
          this.$router.push({ name: 'home' });
        }
      } else if (responseData.status === 400) {
        this.formErrors = responseData.body;
      } else {
        this.formErrors = {
          non_field_errors: [
            'Unable to communicate with the server. Please check your '
            + 'connection and try again.',
          ],
        };
      }
    },
    addTag(name) {
      this.formFields.tags.push({ name });
      this.current_tag = '';
    },
    tabber(event) {
      if (event) {
        event.preventDefault();
        const startText = this.formFields.description.slice(0, event.target.selectionStart);
        const endText = this.formFields.description.slice(event.target.selectionStart);
        this.formFields.description = `${startText}\t${endText}`;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.row {
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
}
.form-group {
  margin: 0;
  margin-bottom: 1rem;
}
.name {
  padding: 5px 10px;
  flex: 1 1 auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

input[type=file] {
  display: none;
}

.image-preview {
  margin: 5px;
  width: 75px;
  height: 75px;
  border-radius: 10px;
}

.custom-file-upload {
  padding-left: 16px;
  padding-right: 16px;
  font-size: .9rem;
}

.progress {
  justify-content: flex-start;
  border-radius: 100px;
  align-items: center;
  position: relative;
  display: flex;
  height: 40px;
  width: 90%;
}

.progress-value {
  box-shadow: 0 3px 15px rgba(41, 63, 110, 0.15);
  border-radius: 100px;
  background: var(--blue-light);
  height: 8px;
  width: 0;

  &.long {
    animation: load 10s normal forwards;
  }
  &.medium {
    animation: load 3s normal forwards;
  }
  &.short {
    animation: load 1s normal forwards;
  }
}

@keyframes load {
  0% { width: 0; }
  100% { width: 100%; }
}

@media (max-width: 500px) {
  .btn, .custom-file-upload {
    width: 100%;
  }
}
</style>
